import { z } from "zod";
import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Card, CardBody, CardHeader, Divider, Textarea } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { InputField } from "@/features/FormCard/Fields";

import { useEditForm } from "@/features/Edit/hooks";
import { useQueryCacheManager } from "@/hooks/useQueyCache";
import { ClientTables } from "@/lib/clientTables";
import { formatErrorMessages } from "@/lib/utils/data_upload/formatErrorMessages";
import { labelByTimeSerieFileStatus } from "@/lib/utils/data_upload/timeSerieFilesStatus";

const FormSchema = z.object({
  status: z.string().optional(),
  file_name: z.string().optional(),
  provider_config_name: z.string().optional(),
  // needed for technical info card (tihs component use the query cache)
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  error_messages: z.record(z.string(), z.unknown()).nullable().optional(),
  user_name: z.string().optional(),
});

export const ItemSchema = FormSchema.extend({
  id: z.number(),
});
export const itemSchema = ItemSchema.keyof().enum;

export type ItemData = z.infer<typeof ItemSchema>;

export function N1TimeSeriesEditCard({
  id,
  client,
}: {
  id: number;
  client: string;
}) {
  const { t } = useTranslation();

  const itemKey = [
    client,
    `${ClientTables.N1_DATA_UPLOAD}`,
    "get",
    "item",
    id,
  ] as const;
  const { data } = useQueryCacheManager<ItemData>(itemKey);

  const { form, onSubmit } = useEditForm({
    id,
    client,
    table_name: ClientTables.N1_DATA_UPLOAD,
    FormSchema,
  });

  useEffect(() => {
    if (data) form.reset(data);
  }, [data, form]);

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${ClientTables.N1_DATA_UPLOAD}.title`, {
                  ns: "tables",
                })}{" "}
                #{id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={itemSchema.status}
                    label={t(`${ClientTables.N1_DATA_UPLOAD}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: true,
                    }}
                    valueMapper={(value) =>
                      labelByTimeSerieFileStatus(value, t)
                    }
                  />
                  <InputField
                    name={itemSchema.provider_config_name}
                    label={t(
                      `${ClientTables.N1_DATA_UPLOAD}.columns.provider_config_name`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: true,
                    }}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <InputField
                      name={itemSchema.file_name}
                      label={t(
                        `${ClientTables.N1_DATA_UPLOAD}.columns.file_name`,
                        {
                          ns: "tables",
                          defaultValue: "Fichier",
                        },
                      )}
                      control={form.control}
                      fieldOptions={{
                        isDisabled: true,
                      }}
                    />
                  </div>
                </div>
                <div className="flex-1 ml-2">
                  <Controller
                    name={itemSchema.error_messages}
                    control={form.control}
                    render={({ field: { value } }) => (
                      <Textarea
                        label={"Message d'Erreur"}
                        labelPlacement="outside"
                        size="sm"
                        radius="full"
                        variant="bordered"
                        isReadOnly
                        disableAutosize
                        classNames={{
                          input: "h-48 max-h-64 overflow-auto !resize-none",
                        }}
                        value={formatErrorMessages(value)}
                      />
                    )}
                  />
                </div>
              </CardBody>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
}
